import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductInventory extends HTMLElement {
  /** @type {Record<string, {status: string, text: string}> | undefined} */
  #inventoryMap;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * Parses the per-variant status/text map product-inventory.liquid embeds
   * alongside the visible content, keyed by variant id. Cached until the
   * next real update replaces the underlying <script> tag.
   */
  #getInventoryMap() {
    if (!this.#inventoryMap) {
      const script = this.querySelector('[data-inventory-map]');
      try {
        this.#inventoryMap = script?.textContent ? JSON.parse(script.textContent) : {};
      } catch {
        this.#inventoryMap = {};
      }
    }
    return this.#inventoryMap;
  }

  /**
   * Fires synchronously on click, well before the section-rendering fetch
   * that updateInventory() below waits on resolves. When the click was a
   * size selection, the newly picked size's own option value already
   * carries its resolved variant id (variant-main-picker.liquid renders
   * one variant per size, since Color is always either hidden or a
   * separate product listing on this catalog — never a second real axis
   * to disambiguate) — enough to resolve the correct status instantly from
   * the embedded map below, with no need to wait on the fetch at all.
   *
   * Anything we can't confidently resolve this way (a non-size option
   * changing, or the option value simply missing from the map) falls back
   * to the existing hide-until-ready behavior instead of risking a wrong
   * optimistic value.
   * @param {CustomEvent<{resource: {id: string}}>} event
   */
  #onVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id;
    const section = this.closest('.shopify-section, dialog');
    const clickedInput = optionValueId
      ? section?.querySelector(`[data-option-value-id="${optionValueId}"]`)
      : null;
    const variantId = clickedInput?.closest('[data-size-option]') ? clickedInput.dataset.variantId : undefined;
    const entry = variantId ? this.#getInventoryMap()[variantId] : undefined;

    if (entry) {
      const icon = this.querySelector('.product-inventory__icon');
      const text = this.querySelector('.product-inventory__text');
      if (icon) icon.className = `svg-wrapper product-inventory__icon product-inventory__icon-${entry.status}`;
      if (text) text.textContent = entry.text;
      this.dataset.ready = 'true';
    } else {
      this.dataset.ready = 'false';
    }
  };

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });
    this.#inventoryMap = undefined;
    this.dataset.ready = 'true';
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
